import React, { useState } from 'react';
import { X, Wrench, FileText } from 'lucide-react';
import { getBookingsByCustomer } from '../lib/bookings';
import { useBusiness } from '../contexts/BusinessContext';
import toast from 'react-hot-toast';

interface AIToolsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactInfo: string;
  messageType: 'sms' | 'email';
  customerId?: string;
  onOpenBookingModal?: (booking: any) => void;
}

export const AIToolsModal: React.FC<AIToolsModalProps> = ({
  isOpen,
  onClose,
  contactInfo,
  messageType,
  customerId,
  onOpenBookingModal
}) => {
  const { selectedBusiness } = useBusiness();
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  const handleInvoiceTool = async () => {
    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    if (!customerId) {
      toast.error('Customer ID not available');
      return;
    }

    setIsLoadingInvoice(true);
    
    try {
      // Get customer's most recent booking using the customerId
      const bookings = await getBookingsByCustomer(selectedBusiness.id, customerId, { limit: 1 });
      
      if (!bookings || bookings.length === 0) {
        toast.error('No bookings found for this customer');
        return;
      }

      const mostRecentBooking = bookings[0];
      
      // Close AI Tools modal and call parent callback
      onClose();
      
      if (onOpenBookingModal) {
        onOpenBookingModal(mostRecentBooking);
      }
      
      toast.success('Opening payments for most recent booking');
      
    } catch (error) {
      console.error('Error in Invoice tool:', error);
      toast.error('Failed to find booking. Please try again.');
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100">
              <Wrench className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Tools</h2>
              <p className="text-sm text-gray-500">
                Smart suggestions for {messageType === 'email' ? '📧' : '📱'} {contactInfo}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Tools Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Invoice Tool */}
            <button
              onClick={handleInvoiceTool}
              disabled={isLoadingInvoice || !customerId}
              className={`flex items-center p-4 bg-white border-2 rounded-xl transition-all duration-200 disabled:cursor-not-allowed group ${
                !customerId 
                  ? 'border-gray-200 opacity-50' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className={`flex items-center justify-center w-12 h-12 rounded-xl mr-4 ${
                !customerId 
                  ? 'bg-gray-100' 
                  : 'bg-blue-100 group-hover:bg-blue-200'
              }`}>
                <FileText className={`h-6 w-6 ${
                  !customerId ? 'text-gray-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="text-left">
                <h3 className={`text-lg font-semibold mb-1 ${
                  !customerId ? 'text-gray-400' : 'text-gray-900'
                }`}>Invoice</h3>
                <p className={`text-sm ${
                  !customerId ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {!customerId 
                    ? 'Customer not identified' 
                    : isLoadingInvoice 
                      ? 'Finding booking...' 
                      : 'Send customer\'s most recent invoice'
                  }
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>


    </div>
  );
};
